import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumber,
  CountryCode,
} from 'libphonenumber-js';

import { TIMEZONE_TO_REGION } from 'utils/timezoneRegions';

const FALLBACK_REGION = 'US';

export type PhoneCountry = {
  iso2: string;
  dialCode: string;
  name: string;
  flag: string;
};

const isDigit = (ch: string) => ch >= '0' && ch <= '9';

export const countDigits = (str: string): number => {
  let n = 0;
  for (let i = 0; i < str.length; i++) {
    if (isDigit(str[i])) n++;
  }
  return n;
};

export const offsetAfterNthDigit = (str: string, count: number): number => {
  if (count <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < str.length; i++) {
    if (isDigit(str[i]) && ++seen === count) return i + 1;
  }
  return str.length;
};

export const iso2ToFlagEmoji = (iso2: string): string =>
  String.fromCodePoint(
    ...iso2
      .toUpperCase()
      .split('')
      .map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)
  );

export const stripDialCode = (
  internationalFormat: string,
  dialCode: string
): string =>
  internationalFormat.replace(new RegExp('^\\+' + dialCode + '\\s*'), '');

const getDisplayNames = (locale?: string) => {
  if (typeof Intl === 'undefined' || !('DisplayNames' in Intl)) {
    return null;
  }
  try {
    return new (Intl as any).DisplayNames([locale || 'ru'], {
      type: 'region',
    });
  } catch (ex) {
    return null;
  }
};

export const getPhoneCountries = (locale?: string): PhoneCountry[] => {
  const displayNames = getDisplayNames(locale);

  const list = getCountries()
    .map((iso2) => {
      const dialCode = getCountryCallingCode(iso2) as string;
      let name = iso2;

      if (displayNames) {
        try {
          name = displayNames.of(iso2);
        } catch (ex) {
          name = iso2;
        }
      }

      return { iso2, dialCode, name, flag: iso2ToFlagEmoji(iso2) };
    })
    .filter((country) => country.dialCode);

  list.sort((a, b) => a.name.localeCompare(b.name, locale || 'ru'));

  return list;
};

export const detectDefaultCountry = (): string => {
  const supportedRegions = getCountries();

  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const regionByTimeZone = TIMEZONE_TO_REGION[timeZone];
    if (
      regionByTimeZone &&
      (supportedRegions as string[]).includes(regionByTimeZone)
    ) {
      return regionByTimeZone;
    }
  } catch (ex) {
    /* Intl.DateTimeFormat недоступен или таймзона не распозналась */
  }

  try {
    const locale = new (Intl as any).Locale(navigator.language);
    const region = locale.maximize().region;
    if (region && (supportedRegions as string[]).includes(region)) {
      return region;
    }
  } catch (ex) {
    /* Intl.Locale недоступен или локаль не распозналась */
  }

  return FALLBACK_REGION;
};

export const formatPhoneNumber = (
  iso2: CountryCode,
  nationalDigits: string
): string => {
  if (!nationalDigits) return '';

  try {
    const parsed = parsePhoneNumber(nationalDigits, iso2);
    if (!parsed) return nationalDigits;
    return stripDialCode(
      parsed.formatInternational(),
      getCountryCallingCode(iso2) as string
    );
  } catch (ex) {
    return nationalDigits;
  }
};

export const phoneValueToNationalDigits = (
  value: string,
  iso2: CountryCode
): string => {
  const digits = value.replace(/\D/g, '');
  const dialCode = getCountryCallingCode(iso2) as string;
  return digits.startsWith(dialCode) ? digits.slice(dialCode.length) : digits;
};

export const formatE164PhoneNumber = (phone: string): string => {
  if (!phone) return '';

  const digits = phone.replace(/\D/g, '');

  if (!digits) return '';

  try {
    const parsed = parsePhoneNumber(`+${digits}`);
    if (parsed && parsed.isValid()) {
      return parsed.formatInternational();
    }
  } catch (ex) {
    /* Некорректный номер — вернем как есть */
  }

  return `+${digits}`;
};
